import * as Api from '@ViewModels';
import { IRange } from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import { NotesDateFilterOptionValue, NotesFilterOptionValue, NotesSortOptionValue } from '../../../../models';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { useEventLogging } from '../../../../models/Logging';
import { getDisplayName } from '../../../../models/NameUtils';
import { formatDateCriteria, formatDateRangeString } from '../../../../models/UiUtils';
import {
	NotesSearchSortByType,
	invalidateNotesByAccount,
	invalidateNotesByMe,
	invalidateNotesSearch,
	useDeleteNoteMutation,
	useInfiniteNotesSearch,
} from '../../../../queries';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../components-v2/Select';
import { NoteCard } from '../../cards/NoteCard';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../ConfirmationDialog';
import { DateRangePickerSideModal } from '../../DateRangePickerSide';
import { FabCalloutPlaceholder } from '../../FabCalloutPlaceholder';
import { LoadingSpinner } from '../../LoadingSpinner';
import { LocalNotificationObserver } from '../../LocalNotificationObserver';
import { Portal } from '../../Portal';
import { NotesPlaceholderIcon } from '../../svgs/icons/NotesPlaceholderIcon';
import { SearchIcon } from '../../svgs/icons/SearchIcon';
import { WarningIcon } from '../../svgs/icons/WarningIcon';
import { TextInput } from '../../TextInput';
import { UserSelectModal } from '../../UserSelectModal';
import { styleSheet } from './styles';

export const NoteOptions: { label: string; value: NotesFilterOptionValue }[] = [
	{ label: 'Show all notes', value: 'byAccount' },
	{ label: 'Show my notes only', value: 'byMe' },
	{ label: 'Show notes owned by...', value: 'ownedBy' },
];

const DefaultDateRangeText = 'Filter by Date';
export const NoteDateOptions: { label: string; value: NotesDateFilterOptionValue }[] = [
	{ label: DefaultDateRangeText, value: 'date' },
];
export const NoteSortOptions: { label: string; value: NotesSortOptionValue }[] = [
	{ label: 'Last Modified date (newest first)', value: 'LastModifiedDate-dsc' },
	{ label: 'Last Modified date (oldest first)', value: 'LastModifiedDate-asc' },
	{ label: 'Creation date (newest first)', value: 'CreationDate-dsc' },
	{ label: 'Creation date (oldest first)', value: 'CreationDate-asc' },
];

interface IProps {
	scrollToBottomPortalId?: string;
	onEditNote: (noteId: string) => void;
}

function _NotesList({ onEditNote, scrollToBottomPortalId }: IProps) {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const { logApiError, logEvent } = useEventLogging();
	const [searchQuery, setSearchQuery] = React.useState('');
	const [debouncedSearchQuery, setDebouncedSearchQuery] = React.useState(searchQuery);
	const [noteIdToDelete, setNoteIdToDelete] = React.useState<string | null>(null);
	const [selectedFilterOption, setSelectedFilterOption] = React.useState(NoteOptions[1]);
	const activeQueryType: 'search' | 'byMe' | 'byAccount' | 'ownedBy' = React.useMemo(() => {
		return debouncedSearchQuery ? 'search' : selectedFilterOption.value;
	}, [selectedFilterOption, debouncedSearchQuery]);
	const [dateRange, setDateRange] = React.useState<IRange<Date | null>>({
		start: null,
		end: null,
	});
	const [showDateSelector, setShowDateSelector] = React.useState(false);
	const [selectedDateRangeText, setSelectedDateRangeText] = React.useState(DefaultDateRangeText);
	const [selectedSortFilterOption, setSelectedSortFilterOption] = React.useState(NoteSortOptions[0]);
	const [showSelectEmployeeModal, setShowSelectEmployeeModal] = React.useState(false);
	const [assignedToUser, setAssignedToUser] = React.useState<Api.UserViewModel | undefined>();

	const filterRequest = React.useMemo(() => {
		const newFilterRequest: Api.IRichContentRequest = {
			criteria: [],
		};

		if (debouncedSearchQuery.length > 0) {
			newFilterRequest.criteria?.push({
				property: Api.RichContentProperty.Content,
				value: debouncedSearchQuery,
			});
		} else {
			newFilterRequest.criteria?.push({
				property: Api.RichContentProperty.All,
				value: 'All',
			});
		}
		if (dateRange?.start) {
			newFilterRequest.criteria?.push(
				formatDateCriteria<Api.RichContentProperty>(Api.RichContentProperty.CreationDate, dateRange)
			);
		}

		if (selectedFilterOption.value === 'ownedBy') {
			newFilterRequest.criteria?.push({
				property: Api.RichContentProperty.OwnedBy,
				value: assignedToUser?.id,
			});
		}

		return newFilterRequest;
	}, [debouncedSearchQuery, dateRange, selectedFilterOption.value, assignedToUser]);
	const sort = React.useMemo<Api.ISortDescriptor<NotesSearchSortByType>>(() => {
		let newSort: 'asc' | 'desc' = 'asc';
		let sortBy: NotesSearchSortByType = 'lastModifiedDate';
		switch (selectedSortFilterOption.value) {
			case 'LastModifiedDate-asc':
				newSort = 'asc';
				sortBy = 'lastModifiedDate';
				break;
			case 'LastModifiedDate-dsc':
				newSort = 'desc';
				sortBy = 'lastModifiedDate';
				break;
			case 'CreationDate-asc':
				newSort = 'asc';
				sortBy = 'creationDate';
				break;
			case 'CreationDate-dsc':
				newSort = 'desc';
				sortBy = 'creationDate';
				break;
			default:
				break;
		}
		return { sort: newSort, sortBy };
	}, [selectedSortFilterOption]);

	const onRangeChangedCallback = (range: IRange<Date | null>) => {
		const nextDateRange = {
			start: range.start,
			end: range.end,
		};

		setDateRange(nextDateRange);
	};

	const onSelectAnEmployeeClose = (user?: Api.UserViewModel, cancelled?: boolean) => {
		if (cancelled) {
			setSelectedFilterOption(NoteOptions[1]);
		} else {
			setSelectedFilterOption(NoteOptions[2]);
			setAssignedToUser(user);
		}
		setShowSelectEmployeeModal(false);
	};

	const notesSearchQuery = useInfiniteNotesSearch({
		currentUserOnly: selectedFilterOption.value === 'byMe',
		enabled: activeQueryType === 'search',
		query: debouncedSearchQuery,
		filterRequest,
		sort: sort.sort,
		sortBy: sort.sortBy,
	});

	const notesByMeQuery = useInfiniteNotesSearch({
		currentUserOnly: selectedFilterOption.value === 'byMe',
		enabled: activeQueryType === 'byMe',
		query: debouncedSearchQuery,
		filterRequest,
		sort: sort.sort,
		sortBy: sort.sortBy,
	});

	const notesByAccountQuery = useInfiniteNotesSearch({
		currentUserOnly: selectedFilterOption.value === 'byMe',
		enabled: activeQueryType === 'byAccount',
		query: debouncedSearchQuery,
		filterRequest,
		sort: sort.sort,
		sortBy: sort.sortBy,
	});

	const notesOwnedByQuery = useInfiniteNotesSearch({
		currentUserOnly: selectedFilterOption.value === 'byMe',
		enabled: activeQueryType === 'ownedBy' && Boolean(assignedToUser),
		query: debouncedSearchQuery,
		filterRequest,
		sort: sort.sort,
		sortBy: sort.sortBy,
	});

	const handleInvalidateAllNotes = () => {
		invalidateNotesByAccount();
		invalidateNotesByMe();
		invalidateNotesSearch();
	};
	const deleteNoteMutation = useDeleteNoteMutation({
		onError: error => {
			logApiError('DeleteNote-Error', error);

			errorMessages.pushApiError(error);
		},
		onSuccess: handleInvalidateAllNotes,
	});
	React.useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedSearchQuery(searchQuery);
		}, 300);
		return () => {
			clearTimeout(handler);
		};
	}, [searchQuery]);
	const activeQuery = {
		byAccount: notesByAccountQuery,
		byMe: notesByMeQuery,
		search: notesSearchQuery,
		ownedBy: notesOwnedByQuery,
	}[activeQueryType];
	if (
		activeQueryType === 'byAccount' &&
		notesByAccountQuery.status === 'success' &&
		notesByAccountQuery.data.pages[0].values.length === 0
	) {
		return (
			<FabCalloutPlaceholder
				icon={<NotesPlaceholderIcon />}
				subtitle='Add your first note with the blue round button on the bottom right.'
				title='You don’t have any notes yet.'
			/>
		);
	}
	const onDeleteNoteConfirmationDialogRequestClose = (
		result?: IConfirmationDialogOption<boolean>,
		canceled?: boolean
	) => {
		if (result && !canceled && noteIdToDelete) {
			logEvent('DeleteNote', { id: noteIdToDelete });

			deleteNoteMutation.mutate({ id: noteIdToDelete });
		}
		setNoteIdToDelete(null);
	};

	return (
		<>
			<div className={css(styleSheet.searchHeader)}>
				<div>
					<div className={css(styleSheet.searchHeaderWrap)}>
						<TextInput
							className={css(styleSheet.searchField)}
							inputId='notes-search'
							leftAccessory={<SearchIcon className={css(styleSheet.searchIcon)} />}
							onChange={ev => setSearchQuery(ev.target.value)}
							placeholder='Search notes'
							type='text'
							value={searchQuery}
						/>
						{debouncedSearchQuery.length > 0 ? (
							<button onClick={() => setSearchQuery('')} className={css(styleSheet.clear)}>
								Clear
							</button>
						) : null}
					</div>
				</div>
				<div className={css(styleSheet.searchContainer)}>
					<Select
						value={selectedSortFilterOption.value}
						onValueChange={value => {
							const option = NoteSortOptions.find(o => o.value === value);
							if (option) {
								setSelectedSortFilterOption(option);
							}
						}}
					>
						<SelectTrigger styles={styleSheet.sortSelect}>
							<span>{selectedSortFilterOption.label}</span>
						</SelectTrigger>
						<SelectContent>
							{NoteSortOptions.map(option => (
								<SelectItem key={option.value} value={option.value}>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<Select
						value={NoteDateOptions[0].value}
						onValueChange={() => {
							if (!dateRange?.start) {
								setDateRange({
									start: new Date(),
									end: new Date(),
								});
							}
							setShowDateSelector(true);
						}}
					>
						<SelectTrigger styles={styleSheet.dateSelect}>
							<span>{selectedDateRangeText}</span>
						</SelectTrigger>
						<SelectContent>
							{NoteDateOptions.map(option => (
								<SelectItem key={option.value} value={option.value}>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<DateRangePickerSideModal
						isOpen={showDateSelector}
						onRequestClose={(clear = false) => {
							setShowDateSelector(false);
							if (clear) {
								setSelectedDateRangeText(DefaultDateRangeText);

								onRangeChangedCallback({ start: null, end: null });
							} else {
								setSelectedDateRangeText(formatDateRangeString(dateRange));
							}
						}}
						onRangeChange={onRangeChangedCallback}
						range={dateRange}
					/>
					<Select
						value={selectedFilterOption.value}
						onValueChange={value => {
							const option = NoteOptions.find(o => o.value === value);
							if (option) {
								setSelectedFilterOption(option);
								if (option.value === 'ownedBy') {
									setShowSelectEmployeeModal(true);
								}
							}
						}}
					>
						<SelectTrigger styles={styleSheet.filterSelect}>
							{selectedFilterOption.value === 'ownedBy' ? (
								<span>Owned by {getDisplayName(assignedToUser) || '...'}</span>
							) : (
								<span>{selectedFilterOption.label}</span>
							)}
						</SelectTrigger>
						<SelectContent>
							{NoteOptions.map(option => (
								<SelectItem key={option.value} value={option.value} styles={styleSheet.filterSelectOption}>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<UserSelectModal
						bodyText='Filter notes by those owned by an employee.'
						// className={`${css(styleSheet.selectAnEmployeeModal)}`}
						modalProps={{
							isOpen: showSelectEmployeeModal,
							onRequestClose: onSelectAnEmployeeClose,
							shouldCloseOnOverlayClick: true,
						}}
						title='Select an Employee'
					/>
				</div>
			</div>

			<div className={css(styleSheet.notesList)}>
				{activeQuery.isPending ? <LoadingSpinner type='large' /> : null}
				{activeQuery.status === 'success'
					? activeQuery.data.pages.map(group =>
							group.values.map(note => {
								const noteVm = new Api.NoteViewModel(userSession, note);
								return (
									<NoteCard
										className={css(styleSheet.note)}
										key={note.id}
										note={noteVm}
										onEditNote={onEditNote}
										onDeleteNote={id => setNoteIdToDelete(id)}
									/>
								);
							})
						)
					: null}
				{activeQuery.hasNextPage && !activeQuery.isFetchingNextPage ? (
					<Portal destination={scrollToBottomPortalId}>
						<Waypoint key='notes-list' bottomOffset='-200px' onEnter={() => activeQuery.fetchNextPage()} />
					</Portal>
				) : null}
				{activeQuery.isFetchingNextPage ? <LoadingSpinner type='small' /> : null}
			</div>
			<LocalNotificationObserver topic={Topics.CREATE_NOTE} onNotificationReceived={handleInvalidateAllNotes} />
			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: !!noteIdToDelete,
					onRequestClose: onDeleteNoteConfirmationDialogRequestClose,
				}}
				options={[
					{
						isDestructive: true,
						representedObject: true,
						title: 'Delete',
					},
					{
						isCancel: true,
						representedObject: false,
						title: 'Cancel',
					},
				]}
				title='Are you sure you want to delete this note?'
			/>
		</>
	);
}

export const NotesList = observer(_NotesList);
